/**
 * Volume Profile & Order Flow Types
 *
 * TypeScript types matching the backend Rust types for volume profile analysis.
 * Used by volumeProfileStore and WebSocketService.
 */

// ============================================================================
// Session Types
// ============================================================================

/**
 * Session type for volume profile aggregation
 *
 * Uses tagged union matching Rust serde(tag = "type", content = "value")
 */
export type SessionType =
  | { type: 'rth' }           // Regular Trading Hours (8:30 AM - 3:15 PM CT)
  | { type: 'eth' }           // Extended Trading Hours (5:02 PM - 8:30 AM CT)
  | { type: 'custom'; value: { startMinutes: number; endMinutes: number } }
  | { type: 'rolling'; value: number };  // Rolling N minutes

// ============================================================================
// Settings
// ============================================================================

/**
 * Volume profile settings (synced with backend)
 */
export interface VolumeProfileSettings {
  enabled: boolean;
  sessionType: SessionType;
  priceTickSize: number;
  showDelta: boolean;
  showCumulativeDelta: boolean;
  showPoc: boolean;
  showValueArea: boolean;
  showVahVal: boolean;
  valueAreaPercent: number;
  updatedAt: number;
}

// ============================================================================
// Volume Data Types
// ============================================================================

/**
 * Volume at a specific price level
 *
 * Tracks bid and ask volume separately to calculate delta (order flow).
 * Delta = askVolume - bidVolume:
 * - Positive delta: More buying pressure (hitting asks)
 * - Negative delta: More selling pressure (hitting bids)
 */
export interface VolumeAtPrice {
  price: number;
  bidVolume: number;
  askVolume: number;
  totalVolume: number;
  delta: number;
}

/**
 * Per-candle footprint data
 *
 * Contains bid/ask volume at each price level within the candle,
 * plus imbalance detection for order flow analysis.
 */
export interface FootprintData {
  timestamp: number;
  levels: VolumeAtPrice[];
  candleDelta: number;
  imbalanceUp: number[];
  imbalanceDown: number[];
}

/**
 * Delta data for cumulative delta line
 *
 * Tracks running sum of delta from session start for
 * divergence and momentum analysis.
 */
export interface DeltaData {
  timestamp: number;
  delta: number;
  cumulativeDelta: number;
}

/**
 * Full volume profile snapshot for a session
 *
 * Contains aggregated volume at each price level with calculated
 * Point of Control (POC) and Value Area.
 */
export interface VolumeProfileSnapshot {
  sessionType: SessionType;
  sessionStart: number;
  sessionEnd: number;
  levels: VolumeAtPrice[];
  pocPrice: number;
  valueAreaHigh: number;
  valueAreaLow: number;
  totalVolume: number;
  totalDelta: number;
}

// ============================================================================
// Defaults
// ============================================================================

/**
 * Default settings matching backend defaults
 */
export const DEFAULT_VOLUME_PROFILE_SETTINGS: VolumeProfileSettings = {
  enabled: true,
  sessionType: { type: 'rolling', value: 30 },
  priceTickSize: 0.25,
  showDelta: true,
  showCumulativeDelta: false,
  showPoc: true,
  showValueArea: true,
  showVahVal: true,
  valueAreaPercent: 70,
  updatedAt: 0,
};

// ============================================================================
// Storage Limits (matching backend)
// ============================================================================

/** Maximum footprint entries to keep per symbol */
export const MAX_FOOTPRINTS_PER_SYMBOL = 500;

/** Maximum delta history points to keep per symbol */
export const MAX_DELTA_POINTS_PER_SYMBOL = 1000;
